import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  CorporateFacilitySubscription,
  FacilitySubscriptionState,
  CorporateSubscriptionInvoiceGroup,
  CorporateSubscriptionInvoiceGroupDetail,
  FacilitySubscriptionProduct,
  FacilitySubscriptionResponse,
  FacilitySubscriptionStatus,
  InvoiceGroupStatus,
  InvoiceGroupType,
  OpenCycleSubscription,
  PagedResultDto,
  SubscriptionInvoice,
  SubscriptionTier,
  toSubscriptionProducts,
  warnOnTierPriceDrift
} from '@umss/shared/data-access';
import { HttpHelper } from '@umss/shared/utils';
import { catchError, map, Observable, of, tap, throwError } from 'rxjs';

/**
 * Filters for the corporate facilities rollup.
 *
 * The API filters by subscription status and by tier; there is no free-text
 * search, so facility names are matched client-side.
 */
export interface CorporateSubscriptionSearch {
  searchTerm?: string;
  status?: FacilitySubscriptionState;
  /** Volume band. The API filters by tier, not by its display range. */
  tier?: SubscriptionTier;
}

/**
 * Filters for the corporate subscription invoices list.
 *
 * Status is the *group's* rolled-up state, not an invoice status: a group of
 * ten facilities is `PartiallyPaid` when some have settled and some have not,
 * which has no single-invoice equivalent.
 */
export interface CorporateInvoiceGroupSearch {
  status?: InvoiceGroupStatus;
  fromDate?: string;
  toDate?: string;
  pageNumber?: number;
  pageSize?: number;
}

/**
 * Every groups request from this service must send this. The endpoint defaults
 * to staffing rather than "all", so omitting it silently returns the wrong
 * groups rather than failing.
 */
export const SUBSCRIPTION_GROUP_TYPE = InvoiceGroupType.Subscription;

/**
 * One row per facility, and the screen filters it client-side by name — so this
 * is not only a page size, it is how far the search can see.
 *
 * A corporation with more facilities than this has a search that silently
 * misses the rest. Raising it trades page weight for reach; fixing it properly
 * needs server-side search, which the endpoint does not offer.
 */
const ROLLUP_PAGE_SIZE = 100;

/**
 * Corporate-level subscription data.
 *
 * The rollup includes facilities that have never subscribed, so a free-tier
 * facility stays visible to the admin who needs to subscribe it.
 *
 * Reading one facility's cycle and toggling its products reuse the *facility*
 * routes, which the API authorizes corporate admins on for facilities of their
 * own corporation — there is no parallel corporate set.
 */
@Injectable({ providedIn: 'root' })
export class CorporateSubscriptionService {
  private readonly http = inject(HttpClient);

  /** Every facility under the corporation with its subscription state. */
  getFacilitySubscriptions(
    search: CorporateSubscriptionSearch = {}
  ): Observable<CorporateFacilitySubscription[]> {
    const term = search.searchTerm?.trim().toLowerCase();

    return this.http
      .get<PagedResultDto<CorporateFacilitySubscription>>(
        'api/corporate/me/subscriptions',
        {
          params: HttpHelper.toHttpParams({
            status: search.status,
            tier: search.tier,
            pageSize: ROLLUP_PAGE_SIZE
          })
        }
      )
      .pipe(
        // The API has no free-text search, so the name filter is applied here.
        map((result) =>
          result.items.filter(
            (facility) =>
              !term || facility.facilityName.toLowerCase().includes(term)
          )
        )
      );
  }

  /** One facility's row, for the corporate detail page header. */
  getFacilitySubscription(
    facilityId: string
  ): Observable<CorporateFacilitySubscription | null> {
    // No single-facility route on the rollup, so the row is picked out of it.
    return this.getFacilitySubscriptions().pipe(
      map(
        (facilities) =>
          facilities.find((facility) => facility.facilityId === facilityId) ??
          null
      )
    );
  }

  /**
   * Subscription invoices for the corporation, grouped per billing cycle.
   *
   * The groups endpoint is shared with staffing and defaults to
   * `InvoiceGroupType.Staffing`, so the request must carry
   * {@link SUBSCRIPTION_GROUP_TYPE} — otherwise the screen quietly lists the
   * weekly staffing groups instead.
   */
  getInvoiceGroups(
    corporationId: string,
    search: CorporateInvoiceGroupSearch = {}
  ): Observable<PagedResultDto<CorporateSubscriptionInvoiceGroup>> {
    return this.http.get<PagedResultDto<CorporateSubscriptionInvoiceGroup>>(
      `api/corporate/${corporationId}/invoice-groups`,
      {
        params: HttpHelper.toHttpParams({
          ...search,
          groupType: SUBSCRIPTION_GROUP_TYPE
        })
      }
    );
  }

  getInvoiceGroup(
    corporationId: string,
    groupId: string
  ): Observable<CorporateSubscriptionInvoiceGroupDetail> {
    return this.http.get<CorporateSubscriptionInvoiceGroupDetail>(
      `api/corporate/${corporationId}/invoice-groups/${groupId}`
    );
  }

  /**
   * One facility's subscription invoice, with its line items.
   *
   * The group route sends members without lines, so opening a facility's row
   * has to come back here for the breakdown. Needs the facility id as well as
   * the invoice id — every subscription route on the API is nested under a
   * facility — which is why the detail route carries both.
   *
   * The per-facility route rather than a corporate parallel, because there is
   * no corporate parallel and none is needed: `FacilityAccessAuthorizationHandler`
   * admits CorporateStaff to any facility, and the service scopes the result to
   * the caller's corporation regardless.
   *
   * No 404 handling: unlike the subscription reads below, a missing invoice is
   * a genuine error rather than the free-tier case.
   */
  getInvoice(
    facilityId: string,
    invoiceId: string
  ): Observable<SubscriptionInvoice> {
    return this.http.get<SubscriptionInvoice>(
      `api/facilities/${facilityId}/subscription/invoices/${invoiceId}`
    );
  }

  /**
   * One facility's product state, with the end date of anything switched off.
   *
   * The rollup reports products as a plain boolean derived from `IsActive`,
   * which stays true through the removal window — so a product on its way out
   * is indistinguishable there from one that is staying. This route carries
   * the deactivation date, so the detail page reads it rather than the rollup.
   */
  getSubscriptions(
    facilityId: string
  ): Observable<FacilitySubscriptionStatus[]> {
    return this.http
      .get<FacilitySubscriptionResponse>(
        `api/facilities/${facilityId}/subscription`
      )
      .pipe(
        map(toSubscriptionProducts),
        // A facility that has never subscribed 404s; that is the free-tier
        // state, not a failure.
        catchError((error: unknown) =>
          error instanceof HttpErrorResponse && error.status === 404
            ? of([] as FacilitySubscriptionStatus[])
            : throwError(() => error)
        )
      );
  }

  /**
   * One facility's open cycle, for the usage widget and the tier ladder.
   *
   * Reuses the facility route rather than a corporate parallel: the API
   * authorizes corporate admins on the facilities of their own corporation.
   *
   * Null where the facility has never subscribed or has no open cycle — the
   * route 404s for both, and that is the routine free-tier state rather than a
   * failure, exactly as on the facility side.
   */
  getOpenCycle(facilityId: string): Observable<OpenCycleSubscription | null> {
    return this.http
      .get<OpenCycleSubscription>(
        `api/facilities/${facilityId}/subscription/open-cycle`
      )
      .pipe(
        tap((cycle) => warnOnTierPriceDrift(cycle.bands)),
        catchError((error: unknown) =>
          error instanceof HttpErrorResponse && error.status === 404
            ? of(null)
            : throwError(() => error)
        )
      );
  }

  /**
   * Turns a product on or off for one of the corporation's facilities.
   *
   * Switching **on** goes through activation: the products route requires a
   * subscription to already exist, so a facility being subscribed for the
   * first time could never use it. Activation creates the subscription where
   * there is none and adds to it where there is, and only ever switches the
   * listed products on — never off.
   */
  setSubscriptionActive(
    facilityId: string,
    product: FacilitySubscriptionProduct,
    active: boolean
  ): Observable<FacilitySubscriptionStatus> {
    const base = `api/facilities/${facilityId}/subscription`;

    // Both routes answer with the whole subscription, so the product is read
    // back out of it rather than assumed: switching off takes effect at the
    // cycle boundary, and only the answer carries that end date.
    const request = active
      ? this.http.post<FacilitySubscriptionResponse>(base, {
          products: [product]
        })
      : this.http.delete<FacilitySubscriptionResponse>(
          `${base}/products/${product}`
        );

    return request.pipe(
      map(
        (response) =>
          toSubscriptionProducts(response).find(
            (status) => status.product === product
          ) ?? { product, active }
      )
    );
  }
}
